import { Controller } from "@hotwired/stimulus"

// A listing (or one option of a store's page) sent to another figure: its
// select's "Another figure…" opens a search (figures#lookup) in `find`; the
// figure picked goes in as "figure:ID", and Escape puts back what it was.
// Every change of the select's value dispatches rw-figure-pick:change, for
// what depends on it (a new figure's line sending its name or the select;
// the inbox's version picker saving). Used by release_watch/links/_page_options,
// the inbox merge box's rows (rw_map_option_field) and _version_picker.
export default class extends Controller {
  static targets = ["select", "find", "query", "results"]
  static values = { lookup: String, self: Number }

  connect() {
    this.selectTarget.dataset.was = this.selectTarget.value
  }

  choose() {
    const select = this.selectTarget
    if (select.value === "__figure__") {
      this.findTarget.hidden = false
      this.queryTarget.focus()
      return
    }
    this.findTarget.hidden = true
    select.dataset.was = select.value
    this.dispatch("change", { target: select })
  }

  search() {
    clearTimeout(this.timer)
    const query = this.queryTarget.value.trim()
    if (query.length < 2) { this.resultsTarget.hidden = true; return }
    this.timer = setTimeout(() => this.fetch(query), 200)
  }

  async fetch(query) {
    const url = new URL(this.lookupValue, window.location.origin)
    url.searchParams.set("q", query)
    const response = await fetch(url, { headers: { Accept: "application/json" } })
    if (!response.ok || query !== this.queryTarget.value.trim()) return
    const figures = (await response.json()).filter(figure => figure.id !== this.selfValue)
    this.resultsTarget.replaceChildren(...figures.map(figure => this.row(figure)))
    this.resultsTarget.hidden = figures.length === 0
    this.active = -1
  }

  row(figure) {
    const button = document.createElement("button")
    button.type = "button"
    button.className = "cx-result"
    button.setAttribute("role", "option")
    const pic = document.createElement(figure.image ? "img" : "span")
    pic.className = "cx-result-pic"
    if (figure.image) { pic.src = figure.image; pic.alt = "" }
    const name = document.createElement("b")
    name.textContent = figure.name
    const chip = document.createElement("span")
    chip.className = "ui-chip brand"
    chip.textContent = figure.brand
    button.append(pic, name, chip)
    button.addEventListener("click", () => this.put(figure))
    return button
  }

  // The picked figure becomes the select's choice.
  put(figure) {
    const select = this.selectTarget
    const value = `figure:${figure.id}`
    let option = [...select.options].find(o => o.value === value)
    if (!option) {
      option = new Option(`Another figure: ${figure.name}`, value)
      select.add(option, [...select.options].find(o => o.value === "__figure__"))
    }
    select.value = value
    select.dataset.was = value
    this.close()
    this.dispatch("change", { target: select })
  }

  key(event) {
    const rows = [...this.resultsTarget.querySelectorAll(".cx-result")]
    if (event.key === "Escape") {
      event.preventDefault()
      this.selectTarget.value = this.selectTarget.dataset.was || ""
      this.close()
      this.selectTarget.focus()
      this.dispatch("change", { target: this.selectTarget })
    } else if (!this.resultsTarget.hidden && rows.length && (event.key === "ArrowDown" || event.key === "ArrowUp")) {
      event.preventDefault()
      this.active = (this.active + (event.key === "ArrowDown" ? 1 : rows.length - 1)) % rows.length
      rows.forEach((row, i) => row.classList.toggle("on", i === this.active))
    } else if (event.key === "Enter") {
      event.preventDefault() // never submits the form from the search
      if (this.active >= 0 && rows[this.active]) rows[this.active].click()
    }
  }

  // A form saved as soon as it's picked (_version_picker): saved once a figure is.
  save(event) {
    if (event.target.value.startsWith("figure:")) event.target.form.requestSubmit()
  }

  close() {
    this.queryTarget.value = ""
    this.resultsTarget.hidden = true
    this.findTarget.hidden = true
  }
}
